import cx from 'classnames';
import { Pane, SplitPane } from 'react-split-pane';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { useDebouncedResizeObserver } from '../../../hooks/useDebouncedResizeObserver';

import 'react-split-pane/styles.css';
import styles from './ResizableFileTreeLayout.module.less';

type RenderTreeArgs = {
  width?: number;
  height?: number;
};

/*
 * Default tree-pane geometry shared by every consumer. Call sites can
 * override any of these, but keeping the common values here avoids
 * repeating the same magic numbers across each layout.
 */
export const DEFAULT_TREE_WIDTH = 250;
export const MIN_TREE_WIDTH = 180;
export const MAX_TREE_WIDTH = 480;

type Props = {
  treeHeader?: ReactNode;
  contentHeader?: ReactNode;
  defaultTreeWidth?: number;
  minTreeWidth?: number;
  maxTreeWidth?: number;
  renderTree: (args: RenderTreeArgs) => ReactNode;
  children: ReactNode;
  className?: string;
};

const toPx = (value: number) => `${value}px`;

/**
 * Two-pane horizontal layout: a resizable file-tree pane on the left,
 * arbitrary content on the right. Wraps `react-split-pane` and measures
 * the tree pane so callers can thread the numeric width into
 * `react-arborist`'s `<Tree width={...}>`.
 *
 * API discipline (to avoid prop bloat over time): the layout owns
 * *layout geometry* — pane sizes, divider, headers — and nothing else.
 * Pane-internal concerns (scroll restoration, focus management, header
 * action buttons, custom chrome) live in the consumer's `renderTree`
 * function or in `children`, never as new top-level props on this
 * component. If a future requirement seems to need a new layout-level
 * prop, first ask "is this really layout, or is the pane consumer the
 * right owner?".
 *
 * Height is intentionally not a prop. `react-split-pane` requires its
 * container to have an explicit height; the consumer provides it via a
 * `className` that sets `height` in their own CSS module. The measured
 * `height` reaches `renderTree` through the same resize-observed wrapper
 * that yields `width`, so trees that need both get both.
 */
export const ResizableFileTreeLayout: VoidFunctionComponent<Props> = ({
  treeHeader,
  contentHeader,
  defaultTreeWidth = DEFAULT_TREE_WIDTH,
  minTreeWidth = MIN_TREE_WIDTH,
  maxTreeWidth = MAX_TREE_WIDTH,
  renderTree,
  children,
  className,
}) => {
  const { width, height, ref: treeRef } = useDebouncedResizeObserver();

  return (
    <SplitPane direction="horizontal" className={cx(styles.layout, className)}>
      <Pane
        defaultSize={toPx(defaultTreeWidth)}
        minSize={toPx(minTreeWidth)}
        maxSize={maxTreeWidth == null ? undefined : toPx(maxTreeWidth)}
      >
        <div className={styles.treePane}>
          {treeHeader && <header className={styles.header}>{treeHeader}</header>}
          <div ref={treeRef} className={styles.treeWrapper}>
            {renderTree({ width, height })}
          </div>
        </div>
      </Pane>
      <Pane>
        <div className={styles.contentPane}>
          {contentHeader && <header className={styles.header}>{contentHeader}</header>}
          {children}
        </div>
      </Pane>
    </SplitPane>
  );
};
